import { React, useEffect } from "react";
import { Typography, Button, Container, Card, Box, Grid, Switch, FormControlLabel } from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import gif1 from "../Assets/consulting.gif";
import gif2 from "../Assets/servinow.gif";
import gif3 from "../Assets/staffing.gif";
import gif4 from "../Assets/webdev.gif";
import InfiniteScroll from "./infiniteScroll";
import { useTheme } from "../Context/ThemeProvider";
import Footer from "./Footer";

const HeroSection = styled(Box)({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "55vh",
    textAlign: "center",
    color: "white",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundImage: "linear-gradient( rgba(10, 9, 59, 0.5) ,rgba(67, 70, 235, 0.5),rgba(10, 9, 59, 0.5))",
        filter: "blur(10px)",
        opacity: 0.6,
        zIndex: -1,
    },
});

const AnimatedButton = motion(Button);
const CardContainer = styled(Container)({ marginTop: "40px" });

const CustomCard = styled(motion(Card))({
    minHeight: "170px",
    padding: "20px",
    textAlign: "center",
    borderRadius: "12px",
    backdropFilter: "blur(10px)",
    backgroundColor: "rgba(52, 39, 153, 0.13)",
    boxShadow: "0px 4px 20px rgba(29, 7, 70, 0.67)",
    border: "1px solid rgba(29, 7, 70, 0.67)",
    color: "white",
});

const Home = () => {

    const { toggleTheme, darkMode } = useTheme();

    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0); 
    }, []);

    return (
        <div>
            <Box sx={{ position: "absolute", top: 60, right: 10, zIndex: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={darkMode}
                            onChange={toggleTheme}
                            color="default"
                        />
                    }
                    label={darkMode ? "Light Mode" : "Dark Mode"}
                    sx={{ color: "white" }}
                />
            </Box>

            <HeroSection>
                <motion.div
                    whileHover={{ textShadow: "0px 0px 8px rgba(255, 255, 255, 0.6), 0px 0px 16px rgba(200, 200, 200, 0.4)" }}
                    transition={{ duration: 0.3 }}
                >
                    <Typography variant="h2" gutterBottom sx={{ color: "white" }}>
                        Welcome to NexExcellency
                    </Typography>
                </motion.div>

                <motion.div
                    whileHover={{ textShadow: "0px 0px 8px rgba(255, 255, 255, 0.6), 0px 0px 16px rgba(200, 200, 200, 0.4)" }}
                    transition={{ duration: 0.3 }}
                >
                    <Typography variant="h5" gutterBottom sx={{ color: "white" }}>
                        Consultancy and Staffing Solutions Expert
                    </Typography>
                </motion.div>

                <AnimatedButton
                    variant="outlined"
                    sx={{ color: "white", borderColor: "white", marginTop: "20px" }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("/career")}
                >
                    Join Us
                </AnimatedButton>
            </HeroSection>

            <CardContainer>
                <motion.div
                    whileHover={{
                        textShadow: "0px 0px 70px rgb(8, 53, 99)",
                        transition: { duration: 0.5 },
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{ color: "#95a9bd", fontFamily: "'Inter', sans-serif", textAlign: "center", mb: 4 }}
                    >
                        Why Us?
                    </Typography>
                </motion.div>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <CustomCard
                            initial={{ opacity: 0, x: -100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            viewport={{ once: true }}
                        >
                            <Typography variant="h5" sx={{ color: "white", fontFamily: "'Inter', sans-serif" }}>
                                Deep understanding of technologies
                            </Typography>
                            <Typography sx={{ fontFamily: "'Inter', sans-serif", color: "#babbbf", textAlign: "left", marginTop: "25px" }}>
                                Technology is about solving human needs through practical application. Our experienced team stays ahead with hands-on expertise in the latest technologies and industry trends, delivering cutting-edge solutions.
                            </Typography>
                        </CustomCard>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <CustomCard
                            initial={{ opacity: 0, x: 100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            viewport={{ once: true }}
                        >
                            <Typography variant="h5" sx={{ color: "#007bff", fontFamily: "'Inter', sans-serif" }}>
                                Recruitment and Sourcing Expert
                            </Typography>
                            <Typography sx={{ fontFamily: "'Inter', sans-serif", color: "#babbbf", textAlign: "left", marginTop: "25px" }}>
                                An organization's success depends on top talent. We excel in sourcing and transforming candidates into valuable assets through experience and innovation.
                            </Typography>
                        </CustomCard>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <CustomCard
                            initial={{ opacity: 0, x: -100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <Typography variant="h5" sx={{ color: "#007bff", fontFamily: "'Inter', sans-serif" }}>
                                Professional Team and Work Culture
                            </Typography>
                            <Typography sx={{ fontFamily: "'Inter', sans-serif", color: "#babbbf", textAlign: "left", marginTop: "25px" }}>
                                We exceed client expectations with high-quality, on-time delivery, aligning the right people, skills, and tools for an exceptional experience.
                            </Typography>
                        </CustomCard>
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <CustomCard
                            initial={{ opacity: 0, x: 100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <Typography variant="h5" sx={{ color: "white", fontFamily: "'Inter', sans-serif" }}>
                                Timely Delivery and Dedicated Support
                            </Typography>
                            <Typography sx={{ fontFamily: "'Inter', sans-serif", color: "#babbbf", textAlign: "left", marginTop: "25px" }}>
                                We exceed expectations with high-quality, on-time delivery, aligning the right people, skills, and tools for an unmatched experience.
                            </Typography>
                        </CustomCard>
                    </Grid>
                </Grid>
            </CardContainer>

            <CardContainer>
                <motion.div
                    whileHover={{
                        textShadow: "0px 0px 70px rgb(8, 53, 99)",
                        transition: { duration: 0.5 },
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{ color: "#95a9bd", fontFamily: "'Inter', sans-serif", textAlign: "center", mb: 4 }}
                    >
                        Our Services
                    </Typography>
                </motion.div>
                <Grid container spacing={3}>
                    {[ 
                        { title: "Consultancy", gif: gif1, description: "Expert strategies and solutions to help your business thrive in a competitive market.", link: "/services/consultancy" },
                        { title: "Staffing", gif: gif3, description: "We connect businesses with top-tier talent to drive innovation and success.", link: "/services/staffing" },
                        { title: "Web Development", gif: gif4, description: "We create modern, responsive, and scalable web applications tailored to your business needs.", link: "/services/web-dev" },
                        { title: "ServiceNow", gif: gif2, description: "Optimizing IT service management with tailored ServiceNow solutions.", link: "/services/servicenow" }
                    ].map((service, index) => (
                        <Grid item xs={12} md={6} key={index} sx={{ mt: index >= 2 ? 6 : 0 }}>
                            <Link to={service.link} style={{ textDecoration: "none" }}>
                                <CustomCard
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                    viewport={{ once: true }}
                                    whileHover={{
                                        boxShadow: "0px 0px 20px rgba(112, 5, 199, 0.9), 0px 0px 40px rgba(68, 6, 104, 0.6)",
                                        transition: { duration: 0.3 }
                                    }}
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        p: 3,
                                        borderRadius: "12px",
                                        boxShadow: 3,
                                        backgroundColor: "rgba(52, 39, 153, 0.13)"
                                    }}
                                >
                                    <Box sx={{ width: "30%", height: "120px", overflow: "hidden", display: "flex", justifyContent: "center", mr: 3 }}>
                                        <img src={service.gif} alt={`${service.title} GIF`} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }} />
                                    </Box>
                                    <Box sx={{ width: "70%" }}>
                                        <Typography variant="h5" sx={{ color: "#007bff", fontFamily: "'Inter', sans-serif" }}>
                                            {service.title}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Inter', sans-serif", color: "#babbbf", textAlign: "left", mt: 1 }}>
                                            {service.description}
                                        </Typography>
                                    </Box>
                                </CustomCard>
                            </Link>
                        </Grid>
                    ))}
                </Grid>
            </CardContainer>
            <div className="customers">
                <Typography
                    variant="h4"
                    sx={{ color: "#95a9bd", fontFamily: "'Inter', sans-serif", textAlign: "center", mb: 4, marginTop: "80px" }}
                >
                     Customers & Partners
                </Typography>
                <InfiniteScroll />
            </div>

            <Box sx={{ backgroundColor: "transparent", color: darkMode ? "#babbbf" : "#000", mt: 10, px: 4, py: 6 }}>
                <Grid container spacing={4} justifyContent="space-between">
                    {/* Company Info */}
                    <Grid item xs={6} md={2.4}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Company</Typography>
                        {[ 
                            { label: "Home", path: "/" },
                            { label: "About Us", path: "/about" },
                            { label: "Careers", path: "/careers" }
                        ].map(item => (
                            <Link key={item.label} to={item.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <Typography variant="body2" sx={{ mb: 1, textDecoration: "underline", cursor: "pointer" }}>
                                    {item.label}
                                </Typography>
                            </Link>
                        ))}
                    </Grid>

                    {/* Services */}
                    <Grid item xs={6} md={2.4}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Services</Typography>
                        {[ 
                            { label: "Staffing", path: "/services/staffing" },
                            { label: "Web Development", path: "/services/web-dev" },
                            { label: "ServiceNow", path: "/services/servicenow" },
                            { label: "Consultancy", path: "/services/consultancy" }
                        ].map(service => (
                            <Link key={service.label} to={service.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <Typography variant="body2" sx={{ mb: 1, textDecoration: "underline", cursor: "pointer" }}>
                                    {service.label}
                                </Typography>
                            </Link>
                        ))}
                    </Grid>

                    <Grid item xs={12} md={2.4}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Connect with us</Typography>
                        <Box sx={{ display: "flex", alignItems: "center !important",justifyContent: "center !important" }}>
                            <a href="https://www.linkedin.com/company/nexexcellency-private-limited" target="_blank" rel="noopener noreferrer">
                                <img src="https://cdn-icons-png.flaticon.com/24/145/145807.png" alt="LinkedIn" style={{ cursor: "pointer" }} />
                            </a>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Footer />
        </div>
    );
};

export default Home;
